'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/* https://randyperkins2k.medium.com/writing-a-simple-markdown-parser-using-javascript-1f2e9449a558 */
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { SmartImage } from "./smartimage";
import { usePixelatedConfig } from "../config/config.client";
import { smartFetch } from "../foundation/smartfetch";
import "./markdown.css";
/* ========== MARKDOWN ========== */
/**
 * Markdown — Renders a simplified markdown string into HTML (supports headings, lists, links, images, inline code).
 *
 * @param {string} [props.markdowndata] - Markdown input string to be parsed and rendered as HTML.
 */
Markdown.propTypes = {
    /** Markdown input (string) to render */
    markdowndata: PropTypes.string.isRequired,
};
export function Markdown(props) {
    function markdownParser(text) {
        const config = usePixelatedConfig();
        const toHTML = text
            .replace(/^#{6}\s(.*$)/gim, '<h6>$1</h6>') // h6 tag
            .replace(/^#{5}\s(.*$)/gim, '<h5>$1</h5>') // h5 tag
            .replace(/^#{4}\s(.*$)/gim, '<h4>$1</h4>') // h4 tag
            .replace(/^#{3}\s(.*$)/gim, '<h3>$1</h3>') // h3 tag
            .replace(/^#{2}\s(.*$)/gim, '<h2>$1</h2>') // h2 tag
            .replace(/^#{1}\s(.*$)/gim, '<h1>$1</h1>') // h1 tag
            .replace(/(=|-|\*){3}/gim, '<hr />') // horizontal rule
            .replace(/!\[(.*?)\]\((.*?)\)/gim, _jsx(SmartImage, { alt: '$1', title: '$1', src: '$2' })) // images
            .replace(/\[([^[]+)\]\((.*)\)/gim, '<a href="$2">$1</a>') // links
            .replace(/^\*{1}\s+(.*$)/gim, '<ul><li>$1</li></ul>') // unordered list
            .replace(/<\/ul>\s?<ul>/g, '') // duplicate unordered list
            .replace(/^\d+\.\s+(.*$)/gim, '<ol><li>$1</li></ol>') // ordered list
            .replace(/<\/ol>\s?<ol>/g, '') // duplicate ordered list
            .replace(/:"(.*?)":/gim, '<q>$1</q>') // quote
            .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>') // blockquote
            .replace(/`(.*?)`/gim, '<code>$1</code>') // inline code
            .replace(/\*{2}(.*?)\*{2}/gim, '<b>$1</b>') // bold text
            .replace(/\*{1}(.*?)\*{1}/gim, '<i>$1</i>') // italic text
            .replace(/~{2}(.*?)~{2}/gim, '<b>$1</b>') // strikethrough
            .replace(/(^[A-z].+)/gim, '<p>$1</p>') // paragraphs
        ;
        return toHTML.trim(); // using trim method to remove whitespace
    }
    return (_jsx("div", { className: "section-container", children: _jsx("div", { className: "markdown", dangerouslySetInnerHTML: { __html: markdownParser(props.markdowndata) } }) }));
}
/* ========== HOOK: useFileData ========== */
/**
 * useFileData — Load markdown or JSON files from /data/ directory
 *
 * @param {string} filePath - Path to file (e.g., '/data/readme.md')
 * @param {string} responseType - 'text' or 'json' (default: 'text')
 * @returns {Object} { data, loading, error }
 */
export function useFileData(filePath, responseType = 'text') {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await smartFetch(filePath, { responseType: responseType });
                setData(result);
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load file');
                setData(null);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [filePath, responseType]);
    return { data, loading, error };
}
